import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import ModalWrapper from './ModalWrapper';
import { CHATBOT_SCENARIOS, NODE_TYPES, ACTION_TYPES } from '../../constants/chatbotScenarios';
import useIdleTimer from '../../hooks/useIdleTimer';
import useReservationLookup from '../../hooks/useReservationLookup';
import useBusinessHours from '../../hooks/useBusinessHours';
import { useDeepLTranslated } from '../../hooks/useDeepLTranslated';

/**
 * 시나리오 챗봇 패널 (공통 컴포넌트)
 *
 * - 시나리오: src/constants/chatbotScenarios.js (정본 = planning/chatbot/07_*.md)
 * - 디자인: planning/chatbot/templates/chatbot-mockup-v2.html (14 STAGE)
 * - 노드 타입: menu / choice / bot / input / lookup / modal / prompt
 *
 * 디자인 요점 (v2 시안 매칭):
 *   • 헤더: 흰 배경 + 오렌지 그라디언트 아바타 + 타이틀/상태 블록 (오렌지 풀바 X)
 *   • 본문: bg-mono-50, 카드 = 흰 배경 + mono-200 보더 + primary-50 호버
 *   • 메인 메뉴: 2열 카드 그리드 + 마지막 풀폭(consultant)
 *   • 선택지: 봇 아바타 + 말풍선, 그 아래 수직 choice-btn 리스트
 *   • 라벨 이모지는 i18n 에서 제거되어 있음 — Icon 컴포넌트만 사용
 */

/* 상태 라벨 — node prefix → 현재 카테고리 (헤더 status 영역) */
const STATUS_BY_PREFIX = [
  ['p_main_menu',  null],   // 운영시간 따라 online / after_hours 동적 결정
  ['p_vehicle',    'chatbot.main_menu.vehicle'],
  ['p_coverage',   'chatbot.main_menu.coverage'],
  ['p_booking',    'chatbot.main_menu.booking'],
  ['p_res',        'chatbot.main_menu.reservation'],
  ['p_meeting',    'chatbot.main_menu.meeting'],
  ['p_loc_share',  'chatbot.main_menu.meeting'],
  ['p_faq',        'chatbot.main_menu.faq'],
  ['p_pay',        'chatbot.main_menu.faq'],
  ['p_consultant', 'chatbot.main_menu.consultant'],
];

const AVATAR_GRADIENT = 'linear-gradient(135deg, #ff8533 0%, #ff6b00 60%, #cc5200 100%)';

/* 운영 상태 색 — 영업중=green / 영업외=amber (limited mode) */
const STATUS_COLOR_OPEN  = '#0bab7c';   // green-500
const STATUS_COLOR_LIMIT = '#f59e0b';   // amber 500

export default function ChatbotPanel({ onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const panelRef = useRef(null);

  const [history, setHistory] = useState([CHATBOT_SCENARIOS.start]);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const [retries, setRetries] = useState(0);
  const [overlayNodeId, setOverlayNodeId] = useState(null);
  const [lastInputValue, setLastInputValue] = useState('');
  // BugHerd #24: 이미지 갤러리 풀스크린 라이트박스 (ModalWrapper 위에 띄움)
  const [lightboxImage, setLightboxImage] = useState(null);  // { src, captionKey, alt }

  const currentNodeId = history[history.length - 1];
  const currentNode = useMemo(
    () => CHATBOT_SCENARIOS.nodes[currentNodeId] || CHATBOT_SCENARIOS.nodes[CHATBOT_SCENARIOS.start],
    [currentNodeId]
  );
  const overlayNode = overlayNodeId ? CHATBOT_SCENARIOS.nodes[overlayNodeId] : null;

  const isRoot = history.length === 1;
  const isInputNode = currentNode.type === NODE_TYPES.INPUT;
  const isLookupNode = currentNode.type === NODE_TYPES.LOOKUP;

  const isReservationLookup = isLookupNode && (currentNode.api?.endpoint || '').includes('/api/reservations/');
  const {
    data: lookupData,
    error: lookupError,
    loading: lookupLoading,
    hoursUntilDeparture,
  } = useReservationLookup(
    isReservationLookup ? lastInputValue : null,
    { enabled: isReservationLookup }
  );
  // LOOKUP 결과를 view_result/cancel_full 등 후속 노드에서도 사용하려면 보존 필요.
  // useReservationLookup 은 LOOKUP 노드 떠나면 enabled=false 로 data null 화.
  const [cachedLookup, setCachedLookup] = useState(null);
  const [cachedHours, setCachedHours] = useState(null);
  useEffect(() => {
    if (lookupData) setCachedLookup(lookupData);
    if (hoursUntilDeparture !== null) setCachedHours(hoursUntilDeparture);
  }, [lookupData, hoursUntilDeparture]);

  /* ── 영업시간 분기 (헤더 dot 색 + after-hours 카피) ── */
  const { isOpen: isBusinessOpen } = useBusinessHours();

  /* ── 헤더 status 라벨 — 노드 prefix 기반 + 운영시간 분기 ── */
  const statusKey = useMemo(() => {
    const hit = STATUS_BY_PREFIX.find(([prefix]) =>
      prefix === currentNodeId || currentNodeId.startsWith(`${prefix}_`)
    );
    // p_main_menu (mapping=null) → 운영시간 따라 동적
    if (hit && hit[1] === null) {
      return isBusinessOpen ? 'chatbot.status.online' : 'chatbot.status.after_hours';
    }
    return hit ? hit[1] : (isBusinessOpen ? 'chatbot.status.online' : 'chatbot.status.after_hours');
  }, [currentNodeId, isBusinessOpen]);

  const statusColor = isBusinessOpen ? STATUS_COLOR_OPEN : STATUS_COLOR_LIMIT;

  /* ── 네비게이션 ── */
  const goTo = useCallback((nodeId) => {
    if (!nodeId || !CHATBOT_SCENARIOS.nodes[nodeId]) {
      // eslint-disable-next-line no-console
      console.warn(`[ChatbotPanel] 정의되지 않은 노드: ${nodeId}`);
      return;
    }
    setInputValue('');
    setInputError('');
    setRetries(0);
    setOverlayNodeId(null);
    setHistory((prev) => [...prev, nodeId]);
  }, []);

  const goBack = useCallback(() => {
    setInputValue('');
    setInputError('');
    setRetries(0);
    setOverlayNodeId(null);
    setHistory((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);

  const goHome = useCallback(() => {
    setInputValue('');
    setInputError('');
    setRetries(0);
    setOverlayNodeId(null);
    setHistory([CHATBOT_SCENARIOS.start]);
  }, []);

  // 🔧 테크프리 가공 (2026-04-30): X 닫기 버튼 즉시 닫기.
  // 이전: p_exit_guard_close 모달 (네/상담원/아니오) 노출 → 사용자 피드백상 불필요.
  // 변경: 사용자가 닫기 의도 표시하면 즉시 종료. 모달 잘림 이슈도 자연 해결.
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const idleEnabled = !isInputNode && !isLookupNode && !overlayNodeId;
  useIdleTimer({
    timeoutMs: 2 * 60 * 1000,
    enabled: idleEnabled,
    watchTarget: panelRef,
    onIdle: () => {
      const idlePromptId = 'p_idle_prompt';
      if (CHATBOT_SCENARIOS.nodes[idlePromptId] && !overlayNodeId) {
        setOverlayNodeId(idlePromptId);
      }
    },
  });

  const resolveLabel = useCallback(
    (item) => (item.labelKey ? t(item.labelKey) : (item.label || '')),
    [t]
  );

  // BugHerd #192: 챗봇 view_result 메시지의 from/to 가 BE raw address (어드민이 한국어로
  // 등록한 투어 step 텍스트 또는 카카오 주소) 라 영어 환경에서도 한국어로 노출되던 회귀.
  // useDeepLTranslated 로 현재 UI 언어에 맞춰 동적 번역. 한국어 환경/한국어 미포함 텍스트는 원문 유지.
  const lookupAddresses = useMemo(() => {
    const d = lookupData || cachedLookup;
    return [d?.route?.departure?.address || '', d?.route?.destination?.address || ''];
  }, [lookupData, cachedLookup]);
  const translatedAddresses = useDeepLTranslated(lookupAddresses);

  const resolveMessage = useCallback(
    (node) => {
      if (!node?.messageKey) return node?.message || '';
      let key = node.messageKey;
      if (currentNodeId === 'p_consultant' && !isBusinessOpen) {
        key = 'chatbot.consultant.after_hours';
      }
      const d = lookupData || cachedLookup;
      const hrs = hoursUntilDeparture !== null ? hoursUntilDeparture : cachedHours;
      let vars = {};
      if (d) {
        // BE ReservationDetail: route.departure.address / route.destination.address,
        //   route.reservation_date, route.departure.time, service_type, status, passengers
        const dep = d.route?.departure || {};
        const dest = d.route?.destination || {};
        const dateStr = d.route?.reservation_date || '';
        const timeStr = (dep.time || '').toString().slice(0, 5);
        const datetime = dateStr ? `${dateStr}${timeStr ? ' ' + timeStr : ''}` : '';
        const svc = d.service_type || '';
        const st = d.status || '';
        vars = {
          id: d.reservation_number || d.id || lastInputValue,
          service_label: svc ? t(`chatbot.service_labels.${svc}`, { defaultValue: svc }) : '',
          // #192: address 는 useDeepLTranslated 결과 우선, 번역 전엔 원문 폴백
          from: translatedAddresses[0] || dep.address || '',
          to: translatedAddresses[1] || dest.address || '',
          datetime,
          passengers: d.passengers != null ? d.passengers : '',
          status_label: st ? t(`chatbot.status_labels.${st}`, { defaultValue: st }) : '',
          hours: hrs !== null ? Math.round(hrs) : '',
        };
      }
      return t(key, vars);
    },
    [t, lookupData, cachedLookup, lastInputValue, hoursUntilDeparture, cachedHours, currentNodeId, isBusinessOpen, translatedAddresses]
  );

  const dispatchAction = useCallback(
    (item) => {
      if (item.action === 'cancel') {
        setOverlayNodeId(null);
        return;
      }
      if (item.next) {
        setOverlayNodeId(null);
        goTo(item.next);
        return;
      }
      if (item.action === ACTION_TYPES.NAVIGATE && item.to) {
        navigate(item.to);
        onClose?.();
      } else if (item.action === ACTION_TYPES.LINK && item.href) {
        window.open(item.href, '_blank', 'noopener,noreferrer');
        onClose?.();
      } else if (item.action === ACTION_TYPES.TEL && item.number) {
        window.location.href = `tel:${item.number}`;
        onClose?.();
      } else if (item.action === 'home') {
        goHome();
      } else if (item.action === 'back') {
        goBack();
      }
    },
    [goTo, goBack, goHome, navigate, onClose]
  );

  const handleInputSubmit = useCallback(
    (e) => {
      e?.preventDefault?.();
      if (currentNode.type !== NODE_TYPES.INPUT) return;
      const val = inputValue.trim();
      if (!val) {
        setInputError(t('chatbot.input_required') || '값을 입력해 주세요.');
        return;
      }
      const regex = new RegExp(currentNode.validate);
      if (regex.test(val)) {
        setLastInputValue(val);
        goTo(currentNode.next);
        return;
      }
      const nextRetries = retries + 1;
      const max = currentNode.maxRetries || 3;
      if (nextRetries >= max) {
        goTo(currentNode.fallback);
        return;
      }
      setRetries(nextRetries);
      setInputError(
        currentNode.errorMessageKey
          ? `${t(currentNode.errorMessageKey)} (${nextRetries}/${max})`
          : `형식이 맞지 않습니다 (${nextRetries}/${max})`
      );
    },
    [currentNode, inputValue, retries, goTo, t]
  );

  useEffect(() => {
    if (!isLookupNode || lookupLoading) return;
    if (lookupData) {
      if (currentNodeId === 'p_res_cancel_lookup' && hoursUntilDeparture !== null && hoursUntilDeparture <= 24) {
        goTo('p_res_cancel_blocked');
      } else {
        goTo(currentNode.onSuccess);
      }
    } else if (lookupError) {
      goTo(currentNode.onError);
    }
  }, [isLookupNode, lookupLoading, lookupData, lookupError, hoursUntilDeparture, currentNodeId, currentNode.onSuccess, currentNode.onError, goTo]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (overlayNodeId) {
          setOverlayNodeId(null);
        } else {
          handleClose();
        }
      }
      if (e.key === 'Backspace' && document.activeElement?.tagName !== 'INPUT') {
        if (!isRoot && !overlayNodeId) {
          e.preventDefault();
          goBack();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose, goBack, isRoot, overlayNodeId]);

  /* ============================== 렌더 ============================== */

  /* 헤더 — 흰 배경 + 그라디언트 아바타 + 타이틀/상태 블록.
   * BugHerd #25: 뒤로가기·홈·닫기 버튼 사이즈 ↑ (w-7→w-9, 닫기 w-8→w-10) + 아이콘 사이즈 ↑ */
  const Header = (
    <div className="h-[60px] px-4 border-b border-orange-100 bg-mono-000 flex items-center justify-between gap-2 shrink-0">
      <div className="flex items-center gap-2.5 min-w-0">
        {!isRoot && (
          <button
            onClick={goBack}
            className="w-9 h-9 rounded-full flex items-center justify-center text-mono-600 hover:bg-orange-50 hover:text-orange-700 transition-colors shrink-0"
            aria-label={t('common.back')}
            title={t('common.back')}
          >
            <Icon name="chevron-left" size={20} />
          </button>
        )}
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0"
          style={{ background: AVATAR_GRADIENT, boxShadow: '0 2px 4px rgba(255,107,0,0.25)' }}
        >
          <Icon name="headset" size={16} />
        </div>
        <div className="flex flex-col leading-tight min-w-0">
          <span className="text-sm font-bold text-mono-900 truncate">
            {t('chatbot.title')}
          </span>
          <span
            className="text-[11px] font-medium flex items-center gap-1 mt-0.5"
            style={{ color: statusColor }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full inline-block"
              style={{ background: statusColor }}
            />
            <span className="truncate">{t(statusKey)}</span>
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!isRoot && (
          <button
            onClick={goHome}
            className="w-9 h-9 rounded-full flex items-center justify-center text-mono-600 hover:bg-orange-50 hover:text-orange-700 transition-colors"
            aria-label={t('common.home') || '처음으로'}
            title={t('common.home') || '처음으로'}
          >
            <Icon name="home" size={18} />
          </button>
        )}
        <button
          onClick={handleClose}
          className="w-10 h-10 rounded-full flex items-center justify-center text-mono-600 hover:bg-orange-50 hover:text-orange-700 transition-colors"
          aria-label={t('common.close')}
          title={t('common.close')}
        >
          <Icon name="close" size={20} />
        </button>
      </div>
    </div>
  );

  /* BugHerd #25 옵션 1: 본문 맨 끝에 시각 분리된 "이전으로" 버튼 — 선택지/메시지 끝에서 항상 노출.
   * 색상·채도·테두리로 선택 버튼과 분리. INPUT/LOOKUP/MENU 루트 노드 제외.
   * 키보드 backspace 도 그대로 작동 (기존). */
  const StickyBackRow = !isRoot && !isInputNode && !isLookupNode ? (
    <div className="pt-1 pb-1 flex justify-center">
      <button
        type="button"
        onClick={goBack}
        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-2xs font-semibold text-mono-500 bg-mono-50 border border-mono-200 hover:bg-mono-100 hover:text-mono-700 hover:border-mono-300 transition-colors"
      >
        <Icon name="chevron-left" size={12} />
        <span>{t('common.back') || '이전으로'}</span>
      </button>
    </div>
  ) : null;

  /* BugHerd #27/#29/#30/#31: 메시지 inline BOLD 마크다운 지원.
   *   "**중요**" → <strong>중요</strong>. 단일 패턴만 파싱하므로 XSS 위험 없음.
   *   `**` 기호가 짝이 안 맞으면 원문 그대로 남김. children 이 string 일 때만 동작. */
  const renderInlineBold = (text) => {
    if (typeof text !== 'string' || !text.includes('**')) return text;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith('**') && p.endsWith('**') && p.length > 4
        ? <strong key={i} className="font-bold text-mono-900">{p.slice(2, -2)}</strong>
        : p
    );
  };

  /* 봇 메시지 — 작은 아바타 + 말풍선 (choice/bot/input/lookup 공통) */
  const BotBubble = ({ children, imageUrl, imageUrls }) => {
    // imageUrls(배열) > imageUrl(단일) 우선. 단일 backward-compatible.
    const images = (imageUrls && imageUrls.length ? imageUrls : (imageUrl ? [imageUrl] : []));
    return (
      <div className="flex items-start gap-2">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0"
          style={{ background: AVATAR_GRADIENT, boxShadow: '0 2px 4px rgba(255,107,0,0.25)' }}
        >
          <Icon name="headset" size={14} />
        </div>
        <div className="bg-mono-000 border border-mono-200 rounded-2xl rounded-tl-md px-3.5 py-2.5 max-w-[calc(100%-36px)] shadow-card">
          {images.length > 0 && (
            <div className={images.length > 1 ? 'grid grid-cols-2 gap-1.5 mb-2' : 'mb-2'}>
              {images.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className={images.length > 1 ? 'w-full aspect-square rounded-lg object-cover' : 'w-full max-w-[180px] rounded-lg object-cover'}
                  loading="lazy"
                />
              ))}
            </div>
          )}
          <p className="text-2sm text-mono-800 leading-relaxed break-keep whitespace-pre-line">
            {renderInlineBold(children)}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={panelRef}
      // #23: 컨텐츠량/뷰포트에 따른 크기 변동 → 완전 고정 사이즈로 변경 (분산되는 느낌 제거)
      //   mobile: h-[85vh] 고정 (반-시트 형태, max-h → h)
      //   desktop: h-[640px] 절대 고정 (sm:max-h 제거 — 뷰포트 변동에도 안 흔들림)
      // 분리감: warm ring(orange-200/60) + 강한 그림자 — 화이트 사이트 위에서도, 모바일 다크탭 위에서도 일관 톤
      className="fixed inset-x-0 bottom-0 z-50 w-full h-[85vh] rounded-t-2xl sm:inset-x-auto sm:bottom-24 sm:right-4 sm:w-[400px] sm:h-[640px] sm:rounded-2xl bg-mono-000 ring-1 ring-orange-200/60 border border-orange-100 overflow-hidden flex flex-col"
      style={{
        animation: 'chatbotSlideIn 0.2s ease-out',
        boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.25), 0 8px 24px -8px rgba(15, 23, 42, 0.15)',
      }}
    >
      <style>{`@keyframes chatbotSlideIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }`}</style>

      {Header}

      {/* Body — #23: warm cream(orange-50/60 ≈ #fff8f1) 으로 은은하게. 카드 hover(풀톤 orange-50)와 톤 차이로 피드백 명확. */}
      <div className="flex-1 overflow-y-auto bg-orange-50/60 px-4 py-4 flex flex-col gap-3">
        {/* MENU — 메인 카테고리 그리드 */}
        {currentNode.type === NODE_TYPES.MENU && (
          <>
            {/* welcome (root 메뉴 전용 — 텍스트 블록, 말풍선 X) */}
            <div className="px-1 pt-1">
              {currentNode.messageKey && (
                <p className="text-[15px] font-bold text-mono-900 leading-snug whitespace-pre-line break-keep">
                  {t(currentNode.messageKey)}
                </p>
              )}
              {currentNode.subMessageKey && (
                <p className="text-xs text-mono-500 mt-1">{t(currentNode.subMessageKey)}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {currentNode.items.map((item, i) => {
                const full = item.full;
                return (
                  <button
                    key={`${item.labelKey || item.label}-${i}`}
                    onClick={() => dispatchAction(item)}
                    className={
                      'group bg-mono-000 border border-mono-200 rounded-xl text-left transition-all hover:border-orange-500 hover:bg-orange-50 hover:shadow-md hover:-translate-y-px ' +
                      (full
                        ? 'col-span-2 flex items-center gap-3 px-3.5 py-3'
                        : 'flex flex-col items-start gap-2 px-3 py-3.5 min-h-[80px]')
                    }
                  >
                    <span
                      className={
                        'rounded-[10px] bg-orange-50 text-orange-500 flex items-center justify-center shrink-0 ' +
                        (full ? 'w-8 h-8' : 'w-9 h-9')
                      }
                    >
                      <Icon name={item.icon} size={full ? 16 : 18} />
                    </span>
                    <span className={'font-semibold text-mono-800 leading-snug ' + (full ? 'text-2sm flex-1' : 'text-[12.5px]')}>
                      {resolveLabel(item)}
                    </span>
                    {full && (
                      <span className="text-mono-400 group-hover:text-orange-500 transition-colors">
                        <Icon name="chevron-right" size={14} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* CHOICE — 봇 메시지 + 수직 선택지 */}
        {currentNode.type === NODE_TYPES.CHOICE && (
          <>
            <BotBubble>{resolveMessage(currentNode)}</BotBubble>
            <div className="flex flex-col gap-1.5 pl-9">
              {(currentNode.choices || []).map((c, i) => (
                <button
                  key={`${c.labelKey || c.label}-${i}`}
                  onClick={() => dispatchAction(c)}
                  className="group bg-mono-000 border border-mono-200 rounded-xl px-3.5 py-2.5 text-left text-2sm font-medium text-mono-800 hover:border-orange-500 hover:bg-orange-50 hover:text-orange-700 hover:shadow-sm transition-all flex items-center gap-2.5"
                >
                  {c.icon && (
                    <span className="text-mono-400 group-hover:text-orange-500 transition-colors shrink-0">
                      <Icon name={c.icon} size={16} />
                    </span>
                  )}
                  <span className="flex-1">{resolveLabel(c)}</span>
                  <span className="text-mono-300 group-hover:text-orange-400 transition-colors shrink-0">
                    <Icon name="chevron-right" size={14} />
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* BOT — 메시지만, next 있으면 진행 버튼 */}
        {currentNode.type === NODE_TYPES.BOT && (
          <>
            <BotBubble imageUrl={currentNode.imageUrl} imageUrls={currentNode.imageUrls}>{resolveMessage(currentNode)}</BotBubble>
            {currentNode.next && (
              <div className="pl-9">
                <button
                  onClick={() => goTo(currentNode.next)}
                  className="px-4 py-2.5 rounded-xl text-2sm font-bold text-white bg-orange-500 hover:bg-orange-600 active:bg-orange-700 transition-colors"
                >
                  {t('common.next') || '다음'}
                </button>
              </div>
            )}
          </>
        )}

        {/* IMAGE_GALLERY — BugHerd #24: 봇 메시지 + 2열 이미지 그리드 + 탭 시 풀스크린 라이트박스.
            외부 카탈로그 링크 새 창 이동 → 사용자 이탈 → inline 표시로 변경. */}
        {currentNode.type === NODE_TYPES.IMAGE_GALLERY && (
          <>
            <BotBubble>{resolveMessage(currentNode)}</BotBubble>
            <div className="pl-9 grid grid-cols-2 gap-2">
              {(currentNode.images || []).map((img, i) => (
                <button
                  key={img.src}
                  type="button"
                  onClick={() => setLightboxImage(img)}
                  className="relative aspect-[4/3] bg-mono-100 rounded-xl overflow-hidden border border-mono-200 hover:border-orange-500 hover:shadow-md transition-all group"
                  aria-label={img.captionKey ? t(img.captionKey) : `image ${i + 1}`}
                >
                  <img
                    src={img.src}
                    alt={img.alt || (img.captionKey ? t(img.captionKey) : '')}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  {img.captionKey && (
                    <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[11px] font-medium px-2 py-1.5 leading-tight">
                      {t(img.captionKey)}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}

        {/* INPUT — 봇 메시지 + 자유 텍스트 + 검증 + 3-strike */}
        {currentNode.type === NODE_TYPES.INPUT && (
          <>
            <BotBubble>{resolveMessage(currentNode)}</BotBubble>
            <form onSubmit={handleInputSubmit} className="pl-9 flex flex-col gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => { setInputValue(e.target.value); setInputError(''); }}
                placeholder={currentNode.placeholder || ''}
                className="w-full px-3.5 py-2.5 rounded-xl bg-mono-000 border-[1.5px] border-orange-500 text-2sm text-mono-900 placeholder:text-mono-400 focus:outline-none focus:ring-4 focus:ring-orange-500/10"
                autoFocus
              />
              {inputError && (
                <p className="text-xs text-red-500 px-1">{inputError}</p>
              )}
              <button
                type="submit"
                className="w-full px-3 py-2.5 rounded-xl text-2sm font-bold text-white bg-orange-500 hover:bg-orange-600 active:bg-orange-700 transition-colors"
              >
                {t('common.submit') || '확인'}
              </button>
            </form>
          </>
        )}

        {/* LOOKUP — API 로딩 + 자동 분기 */}
        {currentNode.type === NODE_TYPES.LOOKUP && (
          <BotBubble>
            <span className="inline-flex gap-1 mr-2 align-middle">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-300 animate-pulse inline-block" />
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse inline-block" style={{ animationDelay: '0.15s' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse inline-block" style={{ animationDelay: '0.3s' }} />
            </span>
            {resolveMessage(currentNode)}
          </BotBubble>
        )}

        {/* BugHerd #25: 본문 끝 "이전으로" 버튼 — root 노드 / input·lookup 노드 제외 */}
        {StickyBackRow}
      </div>

      {/* MODAL overlay */}
      {overlayNode && overlayNode.type === NODE_TYPES.MODAL && (
        <div className="absolute inset-0 bg-black/45 z-10 flex items-end justify-center p-4">
          <div className="w-full bg-mono-000 rounded-2xl p-5 shadow-xl">
            <p className="text-2sm text-mono-800 leading-relaxed break-keep whitespace-pre-line text-center mb-4">
              {overlayNode.messageKey ? t(overlayNode.messageKey) : overlayNode.message}
            </p>
            <div className="flex flex-col gap-1.5">
              {(overlayNode.buttons || []).map((b, i) => {
                const isPrimary = i === 0;
                const isGhost = !b.next && !b.action;
                return (
                  <button
                    key={`${b.labelKey || b.label}-${i}`}
                    onClick={() => {
                      if (isGhost) onClose?.();
                      else dispatchAction(b);
                    }}
                    className={
                      isPrimary
                        ? 'w-full h-10 rounded-xl text-2sm font-bold text-white bg-orange-500 hover:bg-orange-600'
                        : 'w-full h-10 rounded-xl text-2sm font-medium text-mono-700 bg-mono-000 border border-mono-200 hover:bg-mono-50'
                    }
                  >
                    {resolveLabel(b)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* PROMPT (idle 토스트) */}
      {overlayNode && overlayNode.type === NODE_TYPES.PROMPT && (
        <div className="absolute left-3 right-3 bottom-14 z-10 bg-mono-000 border border-orange-200 rounded-xl shadow-lg p-3">
          <p className="text-xs text-mono-800 leading-relaxed break-keep whitespace-pre-line mb-2">
            {overlayNode.messageKey ? t(overlayNode.messageKey) : overlayNode.message}
          </p>
          <div className="flex gap-1.5">
            {(overlayNode.buttons || []).map((b, i) => (
              <button
                key={`${b.labelKey || b.label}-${i}`}
                onClick={() => dispatchAction(b)}
                className={
                  i === 0
                    ? 'flex-1 h-8 rounded-lg text-xs font-bold text-white bg-orange-500 hover:bg-orange-600'
                    : 'flex-1 h-8 rounded-lg text-xs font-medium text-mono-700 bg-mono-000 border border-mono-200 hover:bg-mono-50'
                }
              >
                {resolveLabel(b)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Footer — body 와 동일 cream 톤 + orange-100 디바이더로 일체감 */}
      <div className="px-4 py-2.5 bg-orange-50/60 border-t border-orange-100 shrink-0">
        <p className="text-2xs text-mono-400 text-center">{t('common.business_hours')}</p>
      </div>

      {/* BugHerd #24: 이미지 풀스크린 라이트박스 — ModalWrapper(z-60) 가 챗봇 패널(z-50) 위로 뜸 */}
      {lightboxImage && (
        <ModalWrapper onClose={() => setLightboxImage(null)} maxWidth="modal-lg" padding="p-3 sm:p-5" gap="gap-3">
          <button
            type="button"
            onClick={() => setLightboxImage(null)}
            className="self-end w-9 h-9 rounded-full flex items-center justify-center text-mono-500 hover:bg-mono-100 transition-colors"
            aria-label={t('common.close')}
          >
            <Icon name="close" size={20} />
          </button>
          <img
            src={lightboxImage.src}
            alt={lightboxImage.alt || (lightboxImage.captionKey ? t(lightboxImage.captionKey) : '')}
            className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
          />
          {lightboxImage.captionKey && (
            <p className="text-2sm font-semibold text-mono-800 text-center px-2">
              {t(lightboxImage.captionKey)}
            </p>
          )}
        </ModalWrapper>
      )}
    </div>
  );
}
